import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import './KeyRatiosWidget.css';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import { formatNumber, getCssVariable } from '../../utils/utils';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import { getKeyRatios } from '../../api/company.api';
import ReactECharts from 'echarts-for-react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
export interface KeyRatiosWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  //type?: number;// 1. Gauge, 2. Bar1 3. Bar2
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

interface KeyRatiosData {
  ric: string;
  bell_code: string;
  ROETTM: number | null;
  SECTORROETTM: number | null;
  ROATTM: number | null;
  SECTORROATTM: number | null;
  PBRTTM: number | null;
  SECTORPBRTTM: number | null;
  PERTTM: number | null;
  SECTORPERTTM: number | null;
}

const KeyRatiosWidget = (props: KeyRatiosWidgetProps) => {
  const { onHeightChange } = props;
  const theme = props.theme ?? ThemeType.Direct;
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'keyRatios');
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(widgetRef, WidgetNameSource.KeyRatios, onHeightChange);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [data, setData] = useState<KeyRatiosData | null>(null);

  // Helper function to create gauge chart option
  const createGaugeOption = (
    title: string,
    companyValue: number | null,
    sectorValue: number | null,
    maxValue: number,
    suffix: string,
    companyName: string = props.symbol || 'Company',
  ) => {
    // Handle null values - default to 0 for display
    const safeCompanyValue = companyValue ?? 0;
    const safeSectorValue = sectorValue ?? 0;
    return {
      series: [
        {
          name: title,
          type: 'gauge',
          min: 0,
          max: maxValue,
          splitNumber: 8,
          radius: '60%',
          center: ['50%', '40%'],
          startAngle: 225,
          endAngle: -45,
          anchor: {
            show: true,
            showAbove: true,
            size: 15,
            itemStyle: {
              borderWidth: 5,
              borderColor: '#000000',
            },
          },
          axisLine: {
            show: true,
            lineStyle: {
              width: 40,
              color: [
                // 8 colors for 8 segments , 0 to 1
                [0, '#3C3C4310'],
                [0.125, '#3C3C4320'],
                [0.126, '#FFFFFF'],
                [0.13, '#FFFFFF'],
                [0.375, '#3C3C4330'],
                [0.376, '#FFFFFF'],
                [0.38, '#FFFFFF'],
                [0.625, '#3C3C4340'],
                [0.875, '#3C3C4350'],
                [1, '#3C3C4360'],
              ],
            },
          },
          pointer: {
            show: true,
            itemStyle: {
              color: '#32ADE6',
            },
            width: 10,
            length: '100%',
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: true,
            distance: -65,
            fontSize: 16,
            color: '#3C3C4380',
            formatter: (value: number) => {
              return `${value}${suffix}`;
            },
          },
          title: {
            show: true,
            offsetCenter: [0, '70%'],
            fontSize: 16,
            fontWeight: 700,
            color: '#7F7F7F',
          },
          detail: {
            show: true,
            offsetCenter: [0, '90%'],
            fontSize: 16,
            fontWeight: 700,
            color: '#000000',
            formatter: (value: number) => {
              if (companyValue === null || companyValue === undefined)
                return '-';
              return `${formatNumber(value.toString(), 2, 2)}${suffix}`;
            },
          },
          data: [
            {
              value:
                companyValue !== null
                  ? Math.min(Math.max(companyValue, 0), maxValue)
                  : 0,
              name: title,
            },
          ],
        },
        {
          name: 'Sector',
          type: 'gauge',
          min: 0,
          max: maxValue,
          splitNumber: 6,
          radius: '60%',
          center: ['50%', '40%'],
          startAngle: 200,
          endAngle: -20,
          axisLine: {
            show: false,
          },
          pointer: {
            itemStyle: {
              color: '#9FCADB',
            },
            width: 10,
            length: '100%',
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          title: {
            show: false,
          },
          detail: {
            show: false,
          },
          data: [
            {
              value:
                sectorValue !== null
                  ? Math.min(Math.max(sectorValue, 0), maxValue)
                  : 0,
              name: 'Sector',
            },
          ],
        },
      ],
    };
  };

  // Helper function to create bar chart option (Type 3)
  const createBarOption = (
    title: string,
    subtitle: string,
    companyValue: number | null,
    sectorValue: number | null,
    suffix: string,
    companyName: string = props.symbol || 'Company',
    allowNegative: boolean = false,
  ) => {
    // Handle null values - use 0 for calculations if null
    const safeCompanyValue = companyValue ?? 0;
    const safeSectorValue = sectorValue ?? 0;

    // Calculate domain only from non-null values
    const values = [companyValue, sectorValue].filter(
      (v) => v !== null,
    ) as number[];
    const maxValue =
      values.length > 0 ? Math.max(...values.map((v) => Math.abs(v))) : 1;
    const domainMax = allowNegative ? maxValue * 1.3 : maxValue * 1.2;
    const domainMin = allowNegative ? -domainMax : 0;

    return {
      tooltip: {
        appendTo: 'body',
        confine: true,
        margin: 20,
        outside: false,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        show: true,
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          const lines = params
            .filter((p: any) => p.value !== null && p.value !== undefined)
            .map((p: any) => {
              if (p.value === 0) return '';
              const valStr = `${formatNumber(p.value.toString(), 2, 2)}${suffix}`;
              return `${p.marker} ${p.name}: ${valStr}`;
            });
          return lines.join('<br/>');
        },
      },
      grid: {
        left: 0,
        right: 0,
        top: 20,
        bottom: 0,
        containLabel: false, // Ensures axis labels are not clipped
      },
      xAxis: {
        type: 'category',
        show: true,
        data: [companyName, 'Sector'],
        axisLine: {
          show: true,
          lineStyle: {
            color: '#BDBDBD',
            width: 2,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        show: false,
        type: 'value',
        max: domainMax,
        min: domainMin,
      },
      series: [
        {
          data: [
            {
              value: companyValue ?? 0,
              label: {
                show: companyValue !== null,
                color: '#000000',
                fontSize: 11,
                fontWeight: 'bold',
                formatter: (p: any) =>
                  p.value !== null &&
                  p.value !== undefined &&
                  companyValue !== null
                    ? `${formatNumber(p.value.toString(), 2, 2)}${suffix}`
                    : '',
              },
              itemStyle: {
                color:
                  companyValue !== null
                    ? getCssVariable('--chart-primary', theme)
                    : 'transparent',
              },
            },
            {
              value: sectorValue ?? 0,
              label: {
                show: sectorValue !== null,
                color: '#000000',
                fontSize: 11,
                fontWeight: 'bold',
                formatter: (p: any) =>
                  p.value !== null &&
                  p.value !== undefined &&
                  sectorValue !== null
                    ? `${formatNumber(p.value.toString(), 2, 2)}${suffix}`
                    : '',
              },
              itemStyle: {
                color:
                  sectorValue !== null
                    ? getCssVariable('--chart-septenary', theme)
                    : 'transparent',
              },
            },
          ],
          type: 'bar',
          barWidth: '100%',
          labelLayout: (params: any) => {
            const isNegative = params.text.startsWith('-');
            const isShort = params.rect.height < 15;
            let yPos =
              params.rect.y + (isNegative ? 5 : params.rect.height - 15);

            if (isShort) {
              yPos = isNegative
                ? params.rect.y + params.rect.height + 2
                : params.rect.y - 14;
            }

            return {
              y: yPos,
              fontSize: params.labelRect.width >= params.rect.width ? 10 : 11,
              align: 'center',
            };
          },
        },
      ],
      title: {
        padding: 0,
      },
      legend: {
        padding: 0,
      },
    };
  };

  // Gauge chart options
  const pbrGaugeOption = useMemo(() => {
    if (!data) return {};
    return createGaugeOption('PBR', data.PBRTTM, data.SECTORPBRTTM, 60, 'x');
  }, [data, props.symbol]);

  const perGaugeOption = useMemo(() => {
    if (!data) return {};
    return createGaugeOption('PER', data.PERTTM, data.SECTORPERTTM, 60, 'x');
  }, [data, props.symbol]);

  const roeGaugeOption = useMemo(() => {
    if (!data) return {};
    return createGaugeOption('ROE', data.ROETTM, data.SECTORROETTM, 60, '%');
  }, [data, props.symbol]);

  const roaGaugeOption = useMemo(() => {
    if (!data) return {};
    return createGaugeOption('ROA', data.ROATTM, data.SECTORROATTM, 60, '%');
  }, [data, props.symbol, theme]);

  // Bar chart options (Type 3)
  const pbrBarOption = useMemo(() => {
    if (!data) return {};
    return createBarOption(
      'PBR (MRQ)',
      'Price to book ratio',
      data.PBRTTM,
      data.SECTORPBRTTM,
      'x',
      props.symbol,
      false,
    );
  }, [data, props.symbol, theme]);

  const perBarOption = useMemo(() => {
    if (!data) return {};
    return createBarOption(
      'PER (TTM)',
      'Price to earning ratio',
      data.PERTTM,
      data.SECTORPERTTM,
      'x',
      props.symbol,
      false,
    );
  }, [data, props.symbol, theme]);

  const roeBarOption = useMemo(() => {
    if (!data) return {};
    return createBarOption(
      'ROE (TTM)',
      'Return on equity',
      data.ROETTM,
      data.SECTORROETTM,
      '%',
      props.symbol,
      true,
    );
  }, [data, props.symbol, theme]);

  const roaBarOption = useMemo(() => {
    if (!data) return {};
    return createBarOption(
      'ROA (TTM)',
      'Return on assets',
      data.ROATTM,
      data.SECTORROATTM,
      '%',
      props.symbol,
      true,
    );
  }, [data, props.symbol, theme]);

  const fetchKeyRatiosData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: any = await getKeyRatios(
        props.symbol || '',
        props.token || '',
      );
      if (res && res.data) {
        // Key ratios API returns a single object, not an array
        const keyRatiosData: KeyRatiosData = Array.isArray(res.data)
          ? res.data[0]
          : res.data;
        setData(keyRatiosData);
        setIsEmptyData(
          !keyRatiosData || Object.keys(keyRatiosData).length === 0,
        );
        if (!keyRatiosData || Object.keys(keyRatiosData).length === 0) {
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'KeyRatiosWidget',
            data: null,
          });
        }
      } else {
        setIsEmptyData(true);
        props.onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'KeyRatiosWidget',
          data: null,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch key ratios data');
      console.error('Error fetching key ratios data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [props.token, props.symbol]);

  useEffect(() => {
    fetchKeyRatiosData();
  }, [props.token, props.symbol, fetchKeyRatiosData]);

  const widgetType = 3; // Default to type 3 (Bar)

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="kr-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="kr-content-wrapper">
            {widgetType === 3 ? (
              // Type 3: Bar charts
              <>
                <div className="kr-bars-grid">
                  <div className="kr-bar-item">
                    <div className="kr-bar-item-title">
                      PBR{' '}
                      <span className="kr-bar-item-title-suffix">(MRQ)</span>
                    </div>
                    <div className="kr-bar-item-name">Price to book ratio</div>
                    <div className="kr-bar-item-value">
                      {data?.PBRTTM !== null && data?.PBRTTM !== undefined
                        ? `${formatNumber(data.PBRTTM.toString(), 2, 2)}x`
                        : '- -'}
                    </div>
                    <ReactECharts
                      option={pbrBarOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-bar-item">
                    <div className="kr-bar-item-title">
                      PER{' '}
                      <span className="kr-bar-item-title-suffix">(TTM)</span>
                    </div>
                    <div className="kr-bar-item-name">
                      Price to earning ratio
                    </div>
                    <div className="kr-bar-item-value">
                      {data?.PERTTM !== null && data?.PERTTM !== undefined
                        ? `${formatNumber(data.PERTTM.toString(), 2, 2)}x`
                        : '- -'}
                    </div>
                    <ReactECharts
                      option={perBarOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-bar-item">
                    <div className="kr-bar-item-title">
                      ROE{' '}
                      <span className="kr-bar-item-title-suffix">(TTM)</span>
                    </div>
                    <div className="kr-bar-item-name">Return on equity</div>
                    <div className="kr-bar-item-value">
                      {data?.ROETTM !== null && data?.ROETTM !== undefined
                        ? `${formatNumber(data.ROETTM.toString(), 2, 2)}%`
                        : '- -'}
                    </div>
                    <ReactECharts
                      option={roeBarOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-bar-item">
                    <div className="kr-bar-item-title">
                      ROA{' '}
                      <span className="kr-bar-item-title-suffix">(TTM)</span>
                    </div>
                    <div className="kr-bar-item-name">Return on assets</div>
                    <div className="kr-bar-item-value">
                      {data?.ROATTM !== null && data?.ROATTM !== undefined
                        ? `${formatNumber(data.ROATTM.toString(), 2, 2)}%`
                        : '- -'}
                    </div>
                    <ReactECharts
                      option={roaBarOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
                <div className="kr-legend">
                  <div className="kr-legend-item">
                    <div
                      className="kr-legend-color"
                      style={{
                        backgroundColor: getCssVariable(
                          '--chart-primary',
                          theme,
                        ),
                      }}
                    ></div>
                    <span className="kr-legend-label">
                      {props.symbol || 'Company'}
                    </span>
                  </div>
                  <div className="kr-legend-item">
                    <div
                      className="kr-legend-color"
                      style={{
                        backgroundColor: getCssVariable(
                          '--chart-septenary',
                          theme,
                        ),
                      }}
                    ></div>
                    <span className="kr-legend-label">Sector</span>
                  </div>
                </div>
              </>
            ) : (
              // Type 1: Gauge charts (default)
              <>
                <div className="kr-gauges-grid">
                  <div className="kr-gauge-item">
                    <ReactECharts
                      option={pbrGaugeOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-gauge-item">
                    <ReactECharts
                      option={perGaugeOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-gauge-item">
                    <ReactECharts
                      option={roeGaugeOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="kr-gauge-item">
                    <ReactECharts
                      option={roaGaugeOption}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
                <div className="kr-legend">
                  <div className="kr-legend-item">
                    <div
                      className="kr-legend-color"
                      style={{ backgroundColor: '#32ADE6' }}
                    ></div>
                    <span className="kr-legend-label">
                      {props.symbol || 'Company'}
                    </span>
                  </div>
                  <div className="kr-legend-item">
                    <div
                      className="kr-legend-color"
                      style={{ backgroundColor: '#9FCADB' }}
                    ></div>
                    <span className="kr-legend-label">Sector</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KeyRatiosWidget;
